import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Lightbulb } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import { getArticle, ARTICLES, PILLARS } from "@/data/articles";
import { FR_BY_EN_SLUG } from "@/data/articlesFr";
import { ES_BY_EN_SLUG } from "@/data/articlesEs";

const SITE = "https://oceandata.ca";

export default function ArticleView() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const { setOpen } = useSimplify();
  const frSlug = article ? FR_BY_EN_SLUG[article.slug] : null;
  const esSlug = article ? ES_BY_EN_SLUG[article.slug] : null;

  useEffect(() => {
    if (!article) return;
    document.title = article.seoTitle;
    if (!frSlug && !esSlug) return;
    const links = [];
    const addAlternate = (hreflang, href) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    };
    addAlternate("en-CA", `${SITE}/journal/${article.slug}`);
    if (frSlug) addAlternate("fr-CA", `${SITE}/fr/journal/${frSlug}`);
    if (esSlug) addAlternate("es", `${SITE}/es/journal/${esSlug}`);
    addAlternate("x-default", `${SITE}/journal/${article.slug}`);
    return () => links.forEach((el) => el.remove());
  }, [article, frSlug, esSlug]);

  if (!article) return <Navigate to="/journal" replace />;

  const pillarLabel = PILLARS.find((p) => p.id === article.pillar)?.label;
  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        author: { "@type": "Person", name: "Bruno Pouliot", worksFor: { "@type": "Organization", name: "Ocean Data" } },
        publisher: { "@id": "https://oceandata.ca/#org" },
        mainEntityOfPage: `https://oceandata.ca/journal/${article.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Ocean Data", item: "https://oceandata.ca" },
          { "@type": "ListItem", position: 2, name: "The Clarity Journal", item: "https://oceandata.ca/journal" },
          { "@type": "ListItem", position: 3, name: article.title, item: `https://oceandata.ca/journal/${article.slug}` },
        ],
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-white">
      <article className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-10">
            <Link
              to="/journal"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-navy transition-colors"
            >
              <ArrowLeft size={15} /> The Clarity Journal
            </Link>
            {frSlug && (
              <Link
                to={`/fr/journal/${frSlug}`}
                data-testid="article-fr-version-link"
                hrefLang="fr"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand-teal transition-colors"
              >
                Lire en français
              </Link>
            )}
            {esSlug && (
              <Link
                to={`/es/journal/${esSlug}`}
                data-testid="article-es-version-link"
                hrefLang="es"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand-teal transition-colors"
              >
                Leer en español
              </Link>
            )}
          </div>

          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            {pillarLabel} · {article.readTime}
          </p>
          <h1
            data-testid="article-headline"
            className="font-display font-bold tracking-tight leading-[1.08] text-brand-navy text-3xl sm:text-4xl lg:text-5xl mb-8"
          >
            <MaskedLine delay={0.1} testId="article-headline-line">{article.title}</MaskedLine>
          </h1>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-12 italic">
            {article.excerpt}
          </p>

          {article.sections.map((sec, i) => (
            <Reveal key={i}>
              <div className="mb-4">
                {sec.h && (
                  <h2 className="font-display font-semibold tracking-tight text-brand-navy text-xl sm:text-2xl leading-snug mt-10 mb-5">
                    {sec.h}
                  </h2>
                )}
                {sec.paras.map((p, j) => (
                  <p key={j} className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          ))}

          {/* Made to Stick package */}
          <Reveal>
            <div data-testid="article-stick-panel" className="mt-14 rounded-3xl bg-brand-tealsoft border border-brand-tealborder p-7 sm:p-9">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-6">
                <Lightbulb size={14} /> Why this piece sticks
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ["Sticky copy", article.stick.copy],
                  ["Visual idea", article.stick.visual],
                  ["15-second film", article.stick.film],
                  ["Why it sticks", article.stick.why],
                ].map(([label, value], i) => (
                  <div key={i} className="rounded-xl bg-white/70 border border-brand-tealborder/60 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-2">{label}</p>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* calm CTA — natural next step, not an interruption */}
          <Reveal>
            <div className="mt-12 rounded-2xl bg-brand-navy text-white p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <p className="font-display font-medium text-lg leading-snug max-w-sm">
                See the two or three places your business could simplify first.
              </p>
              <button
                data-testid="article-cta-button"
                onClick={() => setOpen(true)}
                className="shrink-0 inline-flex items-center gap-2.5 rounded-full bg-brand-teal text-white text-sm font-semibold px-7 py-3.5 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                Show me where I can simplify
                <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>

          {/* related reading — Norman signifiers */}
          <div className="mt-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-5">
              Keep reading
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/journal/${r.slug}`}
                  data-testid={`article-related-${r.slug}`}
                  className="group rounded-2xl border border-slate-200 p-6 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <p className="font-display font-semibold text-base text-brand-navy leading-snug group-hover:text-brand-teal transition-colors mb-2">
                    {r.title}
                  </p>
                  <p className="text-xs text-slate-500">{r.readTime}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
